"use client";
//import node module libraries
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { v4 as uuid } from "uuid";
import { useDispatch, useSelector } from "react-redux";

//import store types
import { RootState } from "@/store";

//import redux slices
import {
  chatStarted,
  chatEnd,
  setSuggestedQuestion,
  setSuggestedQuestionLoading,
  setSuggestedTemplates,
  setChatSuggestion,
  setSuggestedTemplateLoading,
  setChatLoading,
  showSuggestedTemplate,
  showSuggestedQuestion,
  addMessage,
  addChatBId,
  resetChats,
  updateChatItem,
  setActiveChatId,
  resetAllChat,
  setNewChatIntent,
  setChatLike,
  setIsSampleData,
  setSelectedText,
} from "@/store/featureSlices/chatSlice";
import { ChatMessage, suggestedQuestion } from "@/types";
import {
  setDatabaseLoading,
  setQueryBankLoading,
} from "@/store/featureSlices/databaseSlice";

//import api client (direct API, no server actions)
import { getQuerySuggestions } from "@/lib/api-client";
import { display } from "html2canvas/dist/types/css/property-descriptors/display";
import { setAutoLogin } from "@/store/featureSlices/chatSettingSlice";

interface SuggestedTemplateQuestions {
  q_id: string;
  question: string;
}

const useChat = () => {
  const dispatch = useDispatch();

  //Chat Window
  const handleChatWindowLoading = (value: boolean) => {
    dispatch(setChatLoading(value));
  };

  const isChatWindowLoading = useSelector(
    (state: RootState) => state.chat.isChatLoading
  );

  const isChatStarted = useSelector(
    (state: RootState) => state.chat.isChatStarted
  );

  const chatStartedFn = () => dispatch(chatStarted());
  const chatEndFn = () => dispatch(chatEnd());

  //Suggested Question
  const suggestedQuestions = useSelector(
    (state: RootState) => state.chat.suggestedQuestions
  );

  const hasSuggestedQuestion =
    Array.isArray(suggestedQuestions) && suggestedQuestions.length > 0;

  const isSuggestedQuestion = useSelector(
    (state: RootState) => state.chat.isQuestionVisible
  );

  const isSuggestedQuestionLoading = useSelector(
    (state: RootState) => state.chat.isQuestionLoading
  );

  // const currentChatMode = useSelector(
  //   (state: RootState) => state.chatSetting.chatMode
  // );

  // const handleChatMode = (mode: string) => dispatch(setChatMode(mode));

  const saveSuggestedQuestion = useCallback(
    (question: suggestedQuestion[]) => {
      dispatch(setSuggestedQuestion(question));
    },
    [dispatch]
  );

  const handleSuggestedQuestionLoading = (value: boolean) => {
    dispatch(setSuggestedQuestionLoading(value));
  };

  //Template Question
  const templateQuestions = useSelector(
    (state: RootState) => state.chat.suggestedTemplates
  );

  const isTemplateQuestion = useSelector(
    (state: RootState) => state.chat.isTemplateVisible
  );

  const isTemplateLoading = useSelector(
    (state: RootState) => state.chat.isTemplateLoading
  );

  const hasTemplateQuestion =
    Array.isArray(templateQuestions) && templateQuestions.length > 0;

  const saveTemplateQuestion = useCallback(
    (question: SuggestedTemplateQuestions[]) => {
      dispatch(setSuggestedTemplates(question));
    },
    [dispatch]
  );

  const handleTemplateQuestionLoading = (value: boolean) => {
    dispatch(setSuggestedTemplateLoading(value));
  };

  const toggleTemplateQuestion = (value: boolean) => {
    if (isSuggestedQuestion) {
      dispatch(showSuggestedQuestion(false));
      dispatch(showSuggestedTemplate(value));
    } else {
      dispatch(showSuggestedTemplate(value));
    }
  };

  const toggleSuggestedQuestion = (value: boolean) => {
    if (isTemplateQuestion) {
      dispatch(showSuggestedTemplate(false));
      dispatch(showSuggestedQuestion(value));
    } else {
      dispatch(showSuggestedQuestion(value));
    }
  };

  interface ChatSuggestionProps {
    q_id: string;
    question: string;
  }

  const chatSuggestion = useSelector(
    (state: RootState) => state.chat.chatSuggestion
  );

  // Chat Suggestion
  const saveChatSuggestion = useCallback(
    (question: ChatSuggestionProps[]) => {
      dispatch(setChatSuggestion(question));
    },
    [dispatch]
  );

  // Chat History
  const chats = useSelector((state: RootState) => state.chat.chat_history);

  const activeChatId = useSelector(
    (state: RootState) => state.chat.activeChatId
  );

  const isNewChatIntent = useSelector(
    (state: RootState) => state.chat.isNewChatIntent
  );

  const handleAddChat = (chat: ChatMessage) => {
    dispatch(addMessage(chat));
  };

  const handleAddChatById = (chat: ChatMessage[]) => {
    dispatch(addChatBId(chat));
  };

  const handleResetChat = () => {
    dispatch(resetChats());
  };

  const updateChatItemFn = (data: ChatMessage) => {
    dispatch(updateChatItem({ updateData: data }));
  };

  const handleCurrentChatId = (value: string | null) => {
    dispatch(setActiveChatId({ chat_id: value }));
  };

  const handleResetAllChat = () => {
    dispatch(resetAllChat());
  };

  const handleSetNewChatIntent = (value: boolean) => {
    dispatch(setNewChatIntent(value));
  };

  const handleChatLike = (id: string, isLiked: number) => {
    dispatch(setChatLike({ id: id, isLiked: isLiked }));
  };

  // User Info
  const user = useSelector((state: RootState) => state.user.user);

  const handleSampleData = (value: boolean) => {
    dispatch(setIsSampleData(value));
  };

  const isSampleData = useSelector(
    (state: RootState) => state.chat.isSampleData
  );

  const isAutoLogin = useSelector(
    (state: RootState) => state.chatSetting.isAutoLogin
  );

  const handleAutoLogin = (value: boolean) => {
    dispatch(setAutoLogin(value));
  };

  const handleSelectedText = (text: string) => {
    if (!text.trim()) {
      toast.error("Please select some text to ask Kinetic");
      return;
    }

    const question = `Give me more information about - ${text}`;

    dispatch(setSelectedText(question));
  };

  return {
    user,
    chats,
    activeChatId,
    isSampleData,
    handleResetAllChat,
    handleSetNewChatIntent,
    isNewChatIntent,
    handleCurrentChatId,
    handleAddChat,
    handleAddChatById,
    handleResetChat,
    updateChatItemFn,
    isChatStarted,
    isChatWindowLoading,
    handleChatWindowLoading,
    chatStartedFn,
    chatEndFn,
    suggestedQuestions,
    isSuggestedQuestionLoading,
    hasSuggestedQuestion,
    isSuggestedQuestion,
    saveSuggestedQuestion,
    saveTemplateQuestion,
    handleSuggestedQuestionLoading,
    templateQuestions,
    isTemplateLoading,
    hasTemplateQuestion,
    isTemplateQuestion,
    handleTemplateQuestionLoading,
    toggleTemplateQuestion,
    toggleSuggestedQuestion,
    saveChatSuggestion,
    chatSuggestion,
    handleChatLike,
    handleSampleData,
    handleAutoLogin,
    isAutoLogin,
    handleSelectedText,
  };
};

export default useChat;
